import { useCallback, useId, useRef, useState } from 'react';
import { FolderOpen, Monitor, Upload, UploadCloud } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ProgressBar } from '@/components/ui/progress';
import {
  DOCUMENT_SIGN_MAX_ENVELOPE_SIZE_BYTES,
  DOCUMENT_SIGN_MAX_FILE_SIZE_BYTES,
  DOCUMENT_SIGN_MAX_SOURCE_FILES,
  DOCUMENT_SIGN_SOURCE_MIME_TYPE,
} from '@/lib/api';
import { useUploadDocument, DocumentUploadError } from '../hooks';
import { FiasFilePickerDialog } from './FiasFilePickerDialog';

interface DocumentUploaderProps {
  envelopeId: string;
  /** Number of source files already attached. Used to enforce the per-envelope cap. */
  currentCount: number;
  /** Combined byte size of source files already attached. Used to enforce the
   *  total-envelope-size cap; defaults to 0 (the server enforces it regardless). */
  currentBytes?: number;
  /** Per-envelope cap on source files; defaults to the platform constant. */
  maxFiles?: number;
  disabled?: boolean;
}

interface UploadFeedback {
  fileName: string;
  loaded: number;
  total: number;
}

export function DocumentUploader({
  envelopeId,
  currentCount,
  currentBytes = 0,
  maxFiles = DOCUMENT_SIGN_MAX_SOURCE_FILES,
  disabled = false,
}: DocumentUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const upload = useUploadDocument();
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState<UploadFeedback | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const remaining = Math.max(0, maxFiles - currentCount);
  const isAtCap = remaining === 0;
  const isInteractable = !disabled && !isAtCap && !upload.isPending;

  const uploadFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      setError(null);

      // Collect every skip / failure reason so a multi-file batch surfaces
      // all of them at once rather than only the last.
      const issues: string[] = [];

      const usable = list.slice(0, remaining);
      if (list.length > usable.length) {
        issues.push(
          `Only ${usable.length} of ${list.length} files can be added (${maxFiles} max per envelope).`,
        );
      }

      // Track the envelope's running byte total so each file in a multi-file
      // drop is checked against the total-size cap as the batch progresses.
      let runningTotal = currentBytes;
      for (const file of usable) {
        if (file.type !== DOCUMENT_SIGN_SOURCE_MIME_TYPE) {
          issues.push(`"${file.name}" is not a PDF and was skipped.`);
          continue;
        }
        if (file.size > DOCUMENT_SIGN_MAX_FILE_SIZE_BYTES) {
          issues.push(`"${file.name}" exceeds the 25 MB limit and was skipped.`);
          continue;
        }
        if (runningTotal + file.size > DOCUMENT_SIGN_MAX_ENVELOPE_SIZE_BYTES) {
          issues.push(
            `"${file.name}" would exceed the ${
              DOCUMENT_SIGN_MAX_ENVELOPE_SIZE_BYTES / (1024 * 1024)
            } MB total envelope limit and was skipped.`,
          );
          continue;
        }
        setProgress({ fileName: file.name, loaded: 0, total: file.size });
        try {
          await upload.mutateAsync({
            envelopeId,
            file,
            onProgress: (loaded, total) => setProgress({ fileName: file.name, loaded, total }),
          });
          runningTotal += file.size;
        } catch (err) {
          if (err instanceof DocumentUploadError) {
            issues.push(`"${file.name}": ${err.message}`);
          } else {
            issues.push(`"${file.name}": ${err instanceof Error ? err.message : 'Upload failed'}`);
          }
        } finally {
          setProgress(null);
        }
      }

      if (issues.length > 0) setError(issues.join('\n'));
    },
    [currentBytes, envelopeId, maxFiles, remaining, upload],
  );

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (!isInteractable) return;
    if (e.dataTransfer.files?.length) {
      void uploadFiles(e.dataTransfer.files);
    }
  };

  return (
    <div className="space-y-3">
      <div
        onDragEnter={(e) => {
          e.preventDefault();
          if (isInteractable) setDragActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (isInteractable) setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-6 py-8 text-center transition-colors',
          isInteractable ? 'border-muted-foreground/30' : 'border-muted-foreground/20 opacity-60',
          dragActive && 'border-primary bg-primary/5',
        )}
        aria-describedby={`${inputId}-help`}
      >
        <UploadCloud className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        <div className="text-sm font-medium">
          {isAtCap ? `Maximum ${maxFiles} files reached` : 'Drop files here'}
        </div>
        <div id={`${inputId}-help`} className="text-xs text-muted-foreground">
          Up to 25 MB
        </div>

        {!isAtCap && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="sm" disabled={!isInteractable}>
                <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
                Browse
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              <DropdownMenuItem onSelect={() => inputRef.current?.click()}>
                <Monitor className="mr-2 h-4 w-4" aria-hidden="true" />
                Desktop
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setPickerOpen(true)}>
                <FolderOpen className="mr-2 h-4 w-4" aria-hidden="true" />
                Fias Files
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept="application/pdf"
          multiple
          className="sr-only"
          disabled={!isInteractable}
          onChange={(e) => {
            if (e.target.files?.length) {
              void uploadFiles(e.target.files);
              // Reset so the same file can be re-selected if needed.
              e.target.value = '';
            }
          }}
        />
      </div>

      {progress && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="truncate">Uploading {progress.fileName}</span>
            <span>{Math.round((progress.loaded / Math.max(progress.total, 1)) * 100)}%</span>
          </div>
          <ProgressBar value={(progress.loaded / Math.max(progress.total, 1)) * 100} />
        </div>
      )}

      {error && (
        <p role="alert" className="whitespace-pre-line text-sm text-destructive">
          {error}
        </p>
      )}

      {pickerOpen && (
        <FiasFilePickerDialog
          open
          onOpenChange={setPickerOpen}
          onFilePicked={(file) => void uploadFiles([file])}
        />
      )}
    </div>
  );
}
